import React from "react";
import type { FretboardPattern } from "../../lib/fingeringEngine";
import { NPS_POSITION_LABELS, npsGlyphLifts, type ExploreNpsSelection } from "../../lib/explore3nps";

function PatternDots({
  active,
  noteCount = 3,
}: {
  active: boolean;
  noteCount?: number;
}) {
  const lifts = npsGlyphLifts(noteCount);
  return (
    <div className="flex h-5 items-end justify-center gap-1" aria-hidden>
      {lifts.map((lift, i) => (
        <span
          key={i}
          className="inline-flex shrink-0"
          style={{ transform: `translateY(-${lift}px)` }}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${
              active
                ? i === 0
                  ? "bg-[#f0a8a0] shadow-[0_0_8px_rgba(240,168,160,0.85)]"
                  : "bg-emerald-400 shadow-[0_0_8px_#34d399]"
                : "bg-white/20 group-hover:bg-white/35"
            }`}
          />
        </span>
      ))}
    </div>
  );
}

export interface ExploreNpsPatternSelectorProps {
  selection: ExploreNpsSelection;
  onSelect: (id: ExploreNpsSelection) => void;
  patterns: readonly FretboardPattern[];
}

export const ExploreNpsPatternSelector: React.FC<ExploreNpsPatternSelectorProps> = ({
  selection,
  onSelect,
  patterns,
}) => (
  <div className="mt-6 flex flex-wrap items-end gap-x-8 gap-y-4 border-b border-white/5 pb-6">
    <button
      type="button"
      onClick={() => onSelect("full")}
      className="group flex cursor-pointer flex-col items-center gap-2"
    >
      <span
        className={`text-[10px] font-sans uppercase tracking-widest transition-colors ${
          selection === "full" ? "text-white/80" : "text-white/30 group-hover:text-white/70"
        }`}
      >
        Completo
      </span>
      <div
        className={`flex h-5 w-8 items-center justify-center rounded border transition-colors ${
          selection === "full"
            ? "border-emerald-400/50 bg-white/10 shadow-[0_0_8px_rgba(52,211,153,0.25)]"
            : "border-white/15 bg-white/5 group-hover:border-white/25"
        }`}
      >
        <span className="grid grid-cols-3 gap-0.5 p-0.5 opacity-60" aria-hidden>
          {Array.from({ length: 9 }).map((_, i) => (
            <span key={i} className="h-0.5 w-0.5 rounded-full bg-white/50" />
          ))}
        </span>
      </div>
    </button>

    {patterns.map((fp, idx) => {
      const active = selection === fp.id;
      const label = NPS_POSITION_LABELS[idx] ?? `POS ${idx + 1}`;
      return (
        <button
          key={fp.id}
          type="button"
          onClick={() => onSelect(fp.id)}
          className="group flex cursor-pointer flex-col items-center gap-2"
          title={`Posición ${fp.label} · traste ${fp.position}`}
        >
          <span
            className={`text-[10px] font-sans uppercase tracking-widest transition-colors ${
              active ? "text-white/80" : "text-white/30 group-hover:text-white/70"
            }`}
          >
            {label}
          </span>
          <PatternDots active={active} noteCount={Math.min(3, fp.notes.length)} />
        </button>
      );
    })}
  </div>
);
